<template>
	<div class="steps__step-form">
		<h3 class="h4 h4--b">
			Select client, key contact & collaborators
		</h3>
		<p class="mb-24">
			All fields are required unless indicated as optional.
		</p>
		<form
			class="form form--fixed-fields"
			@submit.prevent="updateForm"
		>
			<InputComponent
				:value="form_fields.client"
				type="button-dropdown"
				input-id="client"
				name="client"
				label="Client company"
				sub-label="Select a client from the database or create a new one"
				placeholder="Search"
				:options="filtered_client_companies"
				filter-key="client"
				:filter-search="form_fields.client.search_query"
				:required="true"
				:errors="form_errors.client || null"
				@update:value="( value ) => handleButtonDropdownUpdate( value, 'client' )"
			/>
			<NewCompanyFormComponent
				field-handle="client"
				form-title="Client company"
				:show-short-code-field="true"
				@refresh:companies="( value, handle ) => updateCompanies( value, handle )"
			/>
			<InputComponent
				:value="form_fields.key_client_contact"
				type="checkboxes-dropdown"
				input-id="key-client-contact"
				name="key-client-contact"
				label="Key client contact"
				sub-label="Select multiple contacts from the list or create a new one"
				placeholder="Search"
				:options="filtered_key_client_contacts"
				:selection-label="selected_key_client_contacts_label"
				filter-key="key_client_contact"
				:filter-search="form_fields.key_client_contact.search_query"
				:active-values="form_fields.key_client_contact.values"
				:required="true"
				:errors="form_errors.key_client_contact || null"
				@update:value="( value ) => handleCheckboxDropdownUpdate( value, 'key_client_contact' )"
			/>
			<NewClientUserFormComponent
				field-handle="key_client_contact"
				form-title="Key client contact"
				@refresh:client-users="( value, handle ) => updateClientUsers( value, handle )"
			/>
			<InputComponent
				:value="form_fields.client_billing_contact"
				type="checkboxes-dropdown"
				input-id="client-billing-contact"
				name="client-billing-contact"
				label="Client billing contact"
				sub-label="Select multiple contacts from the list or create a new one"
				placeholder="Search"
				:options="filtered_client_billing_contacts"
				:selection-label="selected_client_billing_contacts_label"
				filter-key="client_billing_contact"
				:filter-search="form_fields.client_billing_contact.search_query"
				:active-values="form_fields.client_billing_contact.values"
				:required="true"
				:errors="form_errors.client_billing_contact || null"
				@update:value="( value ) => handleCheckboxDropdownUpdate( value, 'client_billing_contact' )"
			/>
			<NewClientUserFormComponent
				field-handle="client_billing_contact"
				form-title="Client billing contact"
				@refresh:client-users="( value, handle ) => updateClientUsers( value, handle )"
			/>
			<InputComponent
				:value="form_fields.email_client_when_live"
				type="checkbox"
				input-id="email-client-when-live"
				name="email-client-when-live"
				label="Send key contact email when project is live"
				:errors="form_errors.email_client_when_live || null"
				@update:value="new_value => form_fields.email_client_when_live = new_value"
			/>
			<InputComponent
				:value="form_fields.clients"
				type="checkboxes-dropdown"
				input-id="clients"
				name="clients"
				label="Additional client contacts"
				sub-label="Add clients here to grant permission to view this project. Optional"
				placeholder="Search"
				:options="filtered_clients"
				:selection-label="selected_clients_label"
				filter-key="clients"
				:filter-search="form_fields.clients.search_query"
				:active-values="form_fields.clients.values"
				:required="true"
				:errors="form_errors.clients || null"
				@update:value="( value ) => handleCheckboxDropdownUpdate( value, 'clients' )"
			/>
			<NewClientUserFormComponent
				field-handle="clients"
				form-title="Client contact"
				@refresh:client-users="( value, handle ) => updateClientUsers( value, handle )"
			/>
			<button
				class="button button--fill"
				type="submit"
			>
				Save {{ stepTitle }}
			</button>
		</form>
	</div>
</template>

<script setup>
import { useNewProjectStore } from '../../stores/new-project.js';
import { ref, computed, defineProps, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import InputComponent from '../InputComponent';
import NewCompanyFormComponent from './NewCompanyFormComponent';
import NewClientUserFormComponent from './NewClientUserFormComponent';

const new_project_store = useNewProjectStore();
const {
	getCompanies,
	getClientUsers,
	validateNewProjectStep,
	setButtonDropdownValue,
	setCheckboxDropdownValue,
	removeCheckboxDropdownValue
} = new_project_store;
const {
	form_fields,
	form_errors,
	filtered_client_companies,
	filtered_key_client_contacts,
	selected_key_client_contacts,
	filtered_client_billing_contacts,
	selected_client_billing_contacts,
	filtered_clients,
	selected_clients,
} = storeToRefs( new_project_store );

defineProps( {
	stepTitle: {
		required: true,
		type: String,
		default: ''
	}
} );

const selected_key_client_contacts_label = computed( () => {
	return selected_key_client_contacts.value
		.map( item => item.label )
		.join( ', ' )
	;
} );

const selected_client_billing_contacts_label = computed( () => {
	return selected_client_billing_contacts.value
		.map( item => item.label )
		.join( ', ' )
	;
} );

const selected_clients_label = computed( () => {
	return selected_clients.value
		.map( item => item.label )
		.join( ', ' )
	;
} );

const loading = ref( true );

onMounted ( async() => {
	await getCompanies();
	await getClientUsers();

	loading.value = false;
} );

async function updateForm() {
	validateNewProjectStep();
}

const handleButtonDropdownUpdate = ( value, handle ) => { // eslint-disable-line snakecasejs/snakecasejs
	setButtonDropdownValue( value, handle );
};

const handleCheckboxDropdownUpdate = ( object, handle ) => { // eslint-disable-line snakecasejs/snakecasejs
	if ( object.checked ) {
		setCheckboxDropdownValue( object.value, handle );
	} else {
		removeCheckboxDropdownValue( object.value, handle );
	}
};

async function updateCompanies( title, handle ) {
	await getCompanies();
	const item = filtered_client_companies.value.find( ( { label } ) => label === title );
	if ( !item || !item.value ) {
		return;
	}
	setButtonDropdownValue( item.value, handle );
}

async function updateClientUsers( full_name, handle ) {
	await getClientUsers();
	if ( !full_name ) {
		return;
	}
	let item = {};
	switch ( handle ) {
		case 'key_client_contact':
			item = filtered_key_client_contacts.value.find( ( { label } ) => label === full_name );
			break;
		case 'client_billing_contact':
			item = filtered_client_billing_contacts.value.find( ( { label } ) => label === full_name );
			break;
		case 'clients':
			item = filtered_clients.value.find( ( { label } ) => label === full_name );
			break;
		default:
			return;
	}
	if ( !item || !item.value ) {
		return;
	}
	setCheckboxDropdownValue( item.value, handle );
}

</script>
